import type { Endpoint } from '../services/apiClient.types';

/**
 * Query keys for the Support Center feature (#1072503).
 * Ensures consistency for React Query caching + invalidation.
 */
export enum SupportCenterQueryKeys {
  TOPICS = 'supportCenterTopics',
  TICKET_PORTALS = 'supportCenterTicketPortals',
  TICKET_CHANNELS = 'supportCenterTicketChannels',
  GUIDES = 'supportCenterGuides',
  QUESTIONS = 'supportCenterQuestions',
  PREFERENCES = 'supportCenterPreferences',
}

const contentRead =
  (path: string, queryKey: SupportCenterQueryKeys) =>
  (baseUrl: string, locale: string): Endpoint => ({
    url: `${baseUrl}/v1.0/Support/${path}?locale=${encodeURIComponent(locale)}`,
    queryKey,
  });

/**
 * Support Center endpoints (Experience API / BFF, base `VITE_EXPERIENCE_API_BASE_URL`),
 * contract `support-center-openapi` 1.5.1.
 *
 * The five content reads embed `locale` in the URL (never `Accept-Language`), so the React Query
 * key (`[queryKey, url]`) changes on language change → automatic refetch. Preferences take none.
 */
export const SUPPORT_CENTER_ROUTES = {
  TOPICS: contentRead('Topics', SupportCenterQueryKeys.TOPICS),
  TICKET_PORTALS: contentRead('TicketPortals', SupportCenterQueryKeys.TICKET_PORTALS),
  TICKET_CHANNELS: contentRead('TicketChannels', SupportCenterQueryKeys.TICKET_CHANNELS),
  GUIDES: contentRead('Guides', SupportCenterQueryKeys.GUIDES),
  QUESTIONS: contentRead('Questions', SupportCenterQueryKeys.QUESTIONS),
  PREFERENCES: (baseUrl: string): Endpoint => ({
    url: `${baseUrl}/v1.0/Support/Preferences`,
    queryKey: SupportCenterQueryKeys.PREFERENCES,
  }),
  SAVE_TICKET_CHANNELS: (baseUrl: string): string =>
    `${baseUrl}/v1.0/Support/Preferences/TicketChannels`,
  RESTORE_TICKET_CHANNEL_DEFAULTS: (baseUrl: string): string =>
    `${baseUrl}/v1.0/Support/Preferences/TicketChannels/RestoreDefault`,
  SAVE_GUIDES: (baseUrl: string): string => `${baseUrl}/v1.0/Support/Preferences/Guides`,
  SAVE_VOTE: (baseUrl: string, questionCode: string): string =>
    `${baseUrl}/v1.0/Support/Preferences/Votes/${encodeURIComponent(questionCode)}`,
} as const;
